import * as navigate from 'app/area/shared/navigation';
import { actionCreators } from 'app/area/risk-matrix-administration/actions';
import { RiskMatrixIds, RiskMatrixTranslation, riskMatrixTranslation } from 'app/area/risk-matrix-administration/constants';
import { RiskMatrixType } from 'app/area/risk-matrix-administration/components/type/risk-matrix-type';
import { withOptions } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import { containerFactory } from 'app/infrastructure/redux';
import React, { Component } from 'react';
import { ElementLoader, Paper, PromptConfirmation } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { PromptProps, withPrompt } from 'app/components';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { RiskMatrixDetails, RiskMatrixState } from 'app/area/risk-matrix-administration/types-store';
import { Options, SetFirstTypeAsDefault } from 'app/area/risk-matrix-administration/types';
import styles from './risk-matrix-administration.scss';
import { ListRendererProps } from 'app/area/codetable/list/list-renderer/types';
import { RiskMatrixStickyHeader } from 'app/area/risk-matrix-administration/components/sticky-header/risk-matrix-sticky-header';
import { routeConstants } from 'app/routes/route-constants';
import { ControlPanel } from 'app/area/risk-matrix-administration/components/control-panel/control-panel';
import { CaseTypes } from 'app/area/risk-matrix-administration/components/case-types/case-types';
import { endpoints } from 'app/area/risk-matrix-administration/endpoints';
import { Matrix } from 'app/area/risk-matrix-administration/components/matrix/matrix';
import { ThirdAxisItemPicker } from './components/third-axis-item-picker/third-axis-item-picker';

type RiskMatrixAdministrationProps = {
  breadcrumbs: number[];
  options: Options;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  translation: RiskMatrixTranslation;
} & typeof actionCreators & RiskMatrixState & ListRendererProps & PromptProps;

interface RiskMatrixAdministrationState {
  shouldDisplayInternalModal: boolean;
  thirdAxisItemPending: ComplexValue<number>;
}

export class RiskMatrixAdministrationComponent extends Component<RiskMatrixAdministrationProps, RiskMatrixAdministrationState> {
  public static statePath: string = STATE.riskMatrix;

  public state: Readonly<RiskMatrixAdministrationState> = {
    shouldDisplayInternalModal: false,
    thirdAxisItemPending: undefined
  };

  public componentDidMount() {
    this.loadAllTypes();
    this.props.clearSelectedCell();
    this.props.clearThirdAxisItem();
    this.props.clearModifiedCells();
  }

  public componentDidUpdate(previousProps: RiskMatrixAdministrationProps) {
    const {
      details, options, type, loadAxes, loadCells, loadDetails, loadRiskAreas, setType,
      fetchingAxes, fetchingCells, fetchingDetails, fetchingRiskAreas, fetchingCaseTypes, clearSelectedCell, thirdAxisItem,
      clearThirdAxisItem, loadCaseTypes
    } = this.props;

    const { type: previousType, details: previousDetails, options: previousOptions,
      thirdAxisItem: previousThirdAxisItem } = previousProps;

    this.setFirstTypeAsDefault({ previousOptions, options, type, setType });
    this.setThirdAxisItemAsDefault();

    if (this.hasComplexValueChanged(previousType, type)) {
      clearSelectedCell();
      clearThirdAxisItem();
      !fetchingDetails && loadDetails(type);
      !fetchingCaseTypes && loadCaseTypes(type);
      !fetchingRiskAreas && loadRiskAreas();
      !fetchingCells && loadCells(type);
    }

    if (this.hasComplexValueChanged(previousThirdAxisItem, thirdAxisItem)) {
      clearSelectedCell();
      !fetchingCells && loadCells(type);
    }

    if (this.hasDetailsChanged(previousDetails, details)) {
      !fetchingAxes && loadAxes(details);
    }
  }

  private is3DMatrix = () => this.props.axes?.length === 3;

  private loadAllTypes = () => this.props.loadOptions(RiskMatrixIds.riskMatrixType,
    endpoints.types(),
    {
      siblingId: undefined,
      levels: undefined,
      displayNotSelected: false
    });

  private setFirstTypeAsDefault = ({ options, setType, type, previousOptions = {} }: SetFirstTypeAsDefault) =>
    !isDefinedIncludingZero(type?.value) && !previousOptions?.riskMatrixType?.codes &&
    options?.riskMatrixType?.codes?.length > 0 && setType(options?.riskMatrixType.codes[0]);

  private setThirdAxisItemAsDefault = () => this.is3DMatrix() && !this.props.thirdAxisItem &&
    !!this.props.axes?.[2]?.items?.[0] && this.setThirdAxisItem(({
      ...this.props.axes[2].items[0], value: this.props.axes[2].items[0].id
    }));

  private hasComplexValueChanged = (previousValue: ComplexValue<number>, value: ComplexValue<number>) =>
    previousValue !== value && isDefinedIncludingZero(value?.value);

  private hasDetailsChanged = (previousDetails: RiskMatrixDetails, details: RiskMatrixDetails) =>
    previousDetails !== details && !!details;

  private isRiskMatrixReady = () => !this.props.fetchingAxes && !this.props.fetchingCells && !this.props.fetchingDetails &&
    !this.props.fetchingRiskAreas && this.props.axes && this.props.cells && this.props.details && this.props.riskAreas;

  private chooseCell = (xAxisItemIndex: number, yAxisItemIndex: number) => {
    this.removeCellFocus();

    this.props.selectCell({
      xAxisItemIndex, yAxisItemIndex, axes: this.props.axes, cells: this.props.cells, thirdAxisItem: this.props.thirdAxisItem
    });
  }

  private removeCellFocus = () => {
    (document.activeElement as HTMLElement).blur();
  }

  private updateSelectedCellRiskWeight = (riskWeight: number = 0) =>
    this.props.updateSelectedCell({
      patch: { riskWeight },
      selectedCellId: this.props.selectedCellId,
      cells: this.props.cells,
      modifiedCells: this.props.modifiedCells
    });

  private updateSelectedCellRiskArea = (riskArea: ComplexValue<number>) =>
    this.props.updateSelectedCell({
      patch: { riskArea: riskArea.value },
      selectedCellId: this.props.selectedCellId,
      cells: this.props.cells,
      modifiedCells: this.props.modifiedCells
    });

  private cancelChanges = () => navigate.replace(routeConstants.selectCodetableAdministration);

  private saveChanges = () => this.props.saveCells(this.props.modifiedCells, this.props.translation.risk_matrix_saved_successfully,
    this.props.type);

  private setThirdAxisItem = (thirdAxisItem: ComplexValue<number>) => {
    if (this.props.modifiedCells.length === 0) {
      this.props.setThirdAxisItem(thirdAxisItem);
      return;
    }

    this.setState({ shouldDisplayInternalModal: true, thirdAxisItemPending: thirdAxisItem });
  }

  private onInternalModalContinue = () => {
    this.props.setThirdAxisItem(this.state.thirdAxisItemPending);
    this.setState({ shouldDisplayInternalModal: false, thirdAxisItemPending: undefined });
  }

  private onInternalModalCancel = () => this.setState({ shouldDisplayInternalModal: false, thirdAxisItemPending: undefined });

  private getSelectedCell = () => {
    const { cells, modifiedCells, selectedCellId } = this.props;

    if (!isDefinedIncludingZero(selectedCellId)) {
      return undefined;
    }

    return modifiedCells.find(cell => cell.id === selectedCellId) ?? cells.find(cell => cell.id === selectedCellId);
  }

  private getMatrixCells = () => {
    const { cells, modifiedCells } = this.props;

    return cells.map(cell => modifiedCells.find(modifiedCell => modifiedCell.id === cell.id) ?? cell);
  }

  public render() {
    const { options, loadOptions, translation, setType, type, axes, riskAreas, topToolbar, thirdAxisItem, caseTypes,
      fetchingCaseTypes } = this.props;

    const selectedCell = this.getSelectedCell();

    const matrixCells = this.getMatrixCells();

    return <div className="entity-view">
      <Paper componentId="risk-matrix-paper" className={undefined}>
        {this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : null}
        {this.state.shouldDisplayInternalModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.onInternalModalContinue}
            onCancel={this.onInternalModalCancel}
          />
          : null
        }
        <RiskMatrixStickyHeader
          cancel={this.cancelChanges}
          save={this.saveChanges}
          translation={translation}
          breadcrumbs={topToolbar}
        />
        <div className={styles.container}>
          <RiskMatrixType
            options={options}
            loadOptions={loadOptions}
            translation={translation}
            onChange={setType}
            value={type}
          />
          <CaseTypes
            fetching={fetchingCaseTypes}
            caseTypes={caseTypes}
            translation={translation}
          />
          <ThirdAxisItemPicker
            setThirdAxisItem={this.setThirdAxisItem}
            thirdAxisItem={thirdAxisItem}
            options={axes?.[2]?.items}
          />
          {
            this.isRiskMatrixReady()
              ? <div className="row">
                <div className="col-md-12 col-lg-7">
                  <Matrix
                    axes={axes}
                    cells={matrixCells}
                    onSelectedCell={this.chooseCell}
                    riskAreas={riskAreas}
                    translation={translation}
                    thirdAxisItem={thirdAxisItem}
                  />
                </div>
                <div className="col-md-12 col-lg-5">
                  <ControlPanel
                    translation={translation}
                    axes={axes}
                    cell={selectedCell}
                    updateSelectedCellRiskArea={this.updateSelectedCellRiskArea}
                    updateSelectedCellRiskWeight={this.updateSelectedCellRiskWeight}
                    riskAreas={riskAreas}
                  />
                </div>
              </div>
              : <ElementLoader />}
        </div>
      </Paper>
    </div>;
  }
}

const selectors = {
  shouldPrompt: (state: RiskMatrixState) => state.modifiedCells.length !== 0
};

const container = containerFactory(
  withOptions(withPrompt(RiskMatrixAdministrationComponent) as any),
  actionCreators,
  selectors,
  riskMatrixTranslation
);

export { container as RiskMatrixAdministration };
